'use client'

import * as React from 'react'

import 'katex/dist/katex.min.css'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'
import { useImagePreview } from '@/hooks/use-image-preview'

import { ImagePreviewModal } from './image-preview-modal'

interface ClickableHtmlContentProps {
  content: string
  className?: string
  clickable?: boolean
  imageClassName?: string
}

export function ClickableHtmlContent({
  content,
  className,
  clickable = true,
  imageClassName = 'img',
}: ClickableHtmlContentProps) {
  const { isOpen, currentImage, openPreview, closePreview } = useImagePreview()
  const contentRef = React.useRef<HTMLDivElement>(null)
  const sanitizedContent = React.useMemo(() => sanitizeHtmlValue(content || ''), [content])

  React.useEffect(() => {
    if (!contentRef.current || !sanitizedContent) return

    const container = contentRef.current
    const cleanupFunctions: Array<() => void> = []

    const renderMathBlocks = async () => {
      const mathBlocks = container.querySelectorAll('.math-block[data-latex]')

      if (mathBlocks.length === 0) return

      try {
        const katex = (await import('katex')).default

        const preprocessLatex = (latex: string) => {
          let processed = latex.replace(/\^(\d+)(?![^{])/g, '^{$1}')
          processed = processed.replace(/_(\d+)(?![^{])/g, '_{$1}')
          processed = processed.replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
          processed = processed.replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')
          processed = processed.replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')
          return processed
        }

        mathBlocks.forEach(block => {
          if (block.hasAttribute('data-math-rendered')) return

          const latex = block.getAttribute('data-latex') || ''
          block.setAttribute('data-math-rendered', 'true')

          try {
            const formulas = latex.split('\n').filter(formula => formula.trim())
            let renderedHTML = ''

            if (formulas.length === 1) {
              const processedLatex = preprocessLatex(latex.trim())
              renderedHTML = katex.renderToString(processedLatex, {
                throwOnError: false,
                displayMode: true,
              })
            } else {
              const renderedFormulas = formulas.map(formula => {
                try {
                  const processedLatex = preprocessLatex(formula.trim())
                  return katex.renderToString(processedLatex, {
                    throwOnError: false,
                    displayMode: true,
                  })
                } catch (error) {
                  return `<div class="math-error">Error: ${formula}</div>`
                }
              })
              renderedHTML = renderedFormulas.join('<br/>')
            }

            block.innerHTML = renderedHTML
          } catch (error) {
            block.innerHTML = `$${latex}$`
          }
        })
      } catch (error) {
        // Failed to load KaTeX
      }
    }

    const renderCodeBlocks = async () => {
      const codeBlocks = container.querySelectorAll('.code-block[data-code]')

      if (codeBlocks.length === 0) return

      try {
        const hljs = (await import('highlight.js')).default

        codeBlocks.forEach(block => {
          if (block.hasAttribute('data-code-rendered')) return

          const code = block.getAttribute('data-code') || ''
          const language = block.getAttribute('data-language') || 'plain'
          block.setAttribute('data-code-rendered', 'true')

          const pre = block.querySelector('pre')
          if (pre) {
            try {
              const highlighted = hljs.highlight(code, { language })
              pre.innerHTML = highlighted.value
              pre.className = `hljs language-${language}`
            } catch (error) {
              pre.textContent = code
              pre.className = 'hljs'
            }
          }
        })
      } catch (error) {
        // Failed to load highlight.js
      }
    }

    const recalculateCodeBlockLineNumbers = () => {
      const codeBlocks = container.querySelectorAll('.code-block[data-code]')

      codeBlocks.forEach(block => {
        const code = block.getAttribute('data-code') || ''
        const codeLines = code.split('\n')

        // Check if this block uses the new line-by-line structure
        const codeContentWrapper = block.querySelector('div > div:nth-child(2)') as HTMLElement
        const lineNumbersContainer = block.querySelector('div > div:first-child') as HTMLElement

        if (codeContentWrapper && lineNumbersContainer) {
          // New structure: line-by-line containers
          const lineContainers = Array.from(
            codeContentWrapper.querySelectorAll('div[data-line-index]')
          ) as HTMLElement[]

          if (lineContainers.length === 0) return

          // Force a reflow to ensure all elements have been measured
          if (lineContainers.length > 0 && lineContainers[0]) {
            void lineContainers[0].offsetHeight
          }

          const LINE_HEIGHT = 19.5
          let cumulativeLineNumber = 1
          lineNumbersContainer.innerHTML = ''

          // Ensure we process all lines, including the last one
          codeLines.forEach((_, logicalIndex) => {
            const lineContainer = lineContainers[logicalIndex]
            if (lineContainer) {
              const height = lineContainer.offsetHeight
              const measuredHeight = height > 0 ? height : LINE_HEIGHT
              const visualLines = Math.max(1, Math.ceil(measuredHeight / LINE_HEIGHT))

              for (let i = 0; i < visualLines; i++) {
                const lineNumberDiv = document.createElement('div')
                lineNumberDiv.style.cssText = `
                  display: block;
                  height: 19.5px;
                  line-height: 1.5;
                `
                lineNumberDiv.textContent = String(cumulativeLineNumber)
                lineNumbersContainer.appendChild(lineNumberDiv)
                cumulativeLineNumber++
              }
            } else {
              // If container doesn't exist yet, still count the line
              const lineNumberDiv = document.createElement('div')
              lineNumberDiv.style.cssText = `
                display: block;
                height: 19.5px;
                line-height: 1.5;
              `
              lineNumberDiv.textContent = String(cumulativeLineNumber)
              lineNumbersContainer.appendChild(lineNumberDiv)
              cumulativeLineNumber++
            }
          })
        } else {
          // Old structure: single pre element - recalculate based on actual code lines
          const pre = block.querySelector('pre')
          const lineNumbersContainerOld = block.querySelector('div > div:first-child') as HTMLElement

          if (pre && lineNumbersContainerOld) {
            // Force a reflow
            void pre.offsetHeight

            const LINE_HEIGHT = 19.5

            // Ensure we count all lines including the last one
            // split('\n') will create an array where:
            // - Each line break creates a new element
            // - If code ends with newline, last element is empty string
            // - If code doesn't end with newline, all lines are included
            const allLines = code.split('\n')
            // Count all lines - split('\n') includes all logical lines
            // If code ends with newline, last element is empty string (still represents a line)
            const actualLineCount = allLines.length
            // If the last element is empty and code ends with newline, it represents a line
            // If code doesn't end with newline, all elements represent lines
            // So we should count all elements
            // But if last element is empty and it's just from trailing newline, we might want to exclude it
            // Actually, for line numbers, we want to count all logical lines
            // So we use allLines.length which includes empty trailing lines from trailing newlines

            // Regenerate line numbers for ALL logical lines
            lineNumbersContainerOld.innerHTML = ''

            // Always generate line numbers for all lines in the code
            for (let index = 0; index < actualLineCount; index++) {
              const lineNumberDiv = document.createElement('div')
              lineNumberDiv.style.cssText = `
                display: block;
                height: 19.5px;
                line-height: 1.5;
              `
              lineNumberDiv.textContent = String(index + 1)
              lineNumbersContainerOld.appendChild(lineNumberDiv)
            }

            // If there are wrapped visual lines, we need to measure the pre element's actual height
            // and add additional line numbers for wrapped segments
            const totalHeight = pre.offsetHeight || pre.scrollHeight
            const expectedHeight = actualLineCount * LINE_HEIGHT
            const wrappedVisualLines = Math.ceil(Math.max(0, totalHeight - expectedHeight) / LINE_HEIGHT)

            if (wrappedVisualLines > 0) {
              for (let i = 0; i < wrappedVisualLines; i++) {
                const lineNumberDiv = document.createElement('div')
                lineNumberDiv.style.cssText = `
                  display: block;
                  height: 19.5px;
                  line-height: 1.5;
                `
                // For wrapped lines, use sequential numbering from last logical line
                lineNumberDiv.textContent = String(actualLineCount + i + 1)
                lineNumbersContainerOld.appendChild(lineNumberDiv)
              }
            }
          }
        }
      })
    }

    const handleImages = () => {
      if (!clickable) return

      const images = container.querySelectorAll(imageClassName) as NodeListOf<HTMLImageElement>

      if (images.length === 0) return

      images.forEach((img, index) => {
        if (img.hasAttribute('data-clickable-processed')) return

        const src = img.src
        const alt = img.alt || `Comment image ${index + 1}`

        img.setAttribute('data-clickable-processed', 'true')

        const originalParent = img.parentNode

        if (!originalParent) return

        const wrapper = document.createElement('div')
        wrapper.className = 'clickable-image-wrapper'
        wrapper.style.cssText = `
          position: relative;
          display: inline-block;
          max-width: min(100%, 220px);
          cursor: pointer;
          border-radius: 8px;
          overflow: hidden;
        `

        const overlay = document.createElement('div')
        overlay.className = 'clickable-image-overlay'
        overlay.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.3);
          opacity: 0;
          transition: opacity 0.2s ease;
          z-index: 1;
        `

        const iconContainer = document.createElement('div')
        iconContainer.style.cssText = `
          background: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          padding: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transform: scale(0.9);
          transition: transform 0.2s ease;
        `

        // Create eye icon using SVG
        iconContainer.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #374151;">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        `

        overlay.appendChild(iconContainer)

        // Style the image
        img.style.cssText = `
          max-width: min(100%, 220px);
          max-height: 160px;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
          border-radius: 8px;
          transition: filter 0.2s ease;
        `

        originalParent.insertBefore(wrapper, img)

        wrapper.appendChild(img)
        wrapper.appendChild(overlay)

        const handleClick = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          openPreview(src, alt)
        }

        const handleMouseEnter = () => {
          overlay.style.opacity = '1'
          iconContainer.style.transform = 'scale(1)'
          img.style.filter = 'brightness(0.8)'
        }

        const handleMouseLeave = () => {
          overlay.style.opacity = '0'
          iconContainer.style.transform = 'scale(0.9)'
          img.style.filter = 'none'
        }

        wrapper.addEventListener('click', handleClick)
        wrapper.addEventListener('mouseenter', handleMouseEnter)
        wrapper.addEventListener('mouseleave', handleMouseLeave)

        cleanupFunctions.push(() => {
          wrapper.removeEventListener('click', handleClick)
          wrapper.removeEventListener('mouseenter', handleMouseEnter)
          wrapper.removeEventListener('mouseleave', handleMouseLeave)

          if (wrapper.parentNode && originalParent) {
            wrapper.parentNode.insertBefore(img, wrapper)
            wrapper.parentNode.removeChild(wrapper)
            img.removeAttribute('data-clickable-processed')
            img.style.cssText = ''
          }
        })
      })
    }

    renderMathBlocks()
    // Recalculate line numbers after code blocks are rendered (renderCodeBlocks is async)
    renderCodeBlocks().then(() => {
      // Wait a bit more after highlighting is complete
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            recalculateCodeBlockLineNumbers()
          })
        })
      }, 100)
    })
    handleImages()

    // Also recalculate after initial delay to catch any edge cases
    const recalculateTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          recalculateCodeBlockLineNumbers()
        })
      })
    }, 300)

    // Recalculate on window resize
    const handleResize = () => {
      recalculateCodeBlockLineNumbers()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(recalculateTimeout)
      window.removeEventListener('resize', handleResize)
      cleanupFunctions.forEach(cleanup => cleanup())
    }
  }, [sanitizedContent, openPreview, clickable, imageClassName])

  return (
    <>
      <div
        ref={contentRef}
        className={cn('clickable-html-content', className)}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        style={{
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          hyphens: 'auto',
        }}
      />

      <style jsx global>{`
        .clickable-html-content .mention,
        .clickable-html-content .ql-mention,
        .clickable-html-content a[data-mention-id] {
          display: inline !important;
          font-weight: 700 !important;
          color: #1d4ed8 !important;
          background: transparent !important;
          text-decoration: none !important;
          cursor: pointer !important;
          white-space: nowrap !important;
        }

        .clickable-html-content .mention:hover,
        .clickable-html-content .ql-mention:hover,
        .clickable-html-content a[data-mention-id]:hover {
          color: #1d4ed8 !important;
          text-decoration: underline !important;
        }

        .clickable-html-content .hashtag {
          background-color: #dbeafe !important;
          color: #1e40af !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
          font-weight: 500 !important;
          white-space: nowrap !important;
        }

        /* Ensure code blocks with line numbers display correctly in comments */
        /* Target code blocks that have line numbers (structure: div containing div + pre) */
        .code-block:not([data-code]) > div {
          display: flex !important;
          background: #282c34 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          margin: 8px 0 !important;
        }

        /* Line numbers container - first child div when code block doesn't have data-code */
        .code-block:not([data-code]) > div > div:first-child {
          display: block !important;
          padding: 8px 8px 8px 12px !important;
          background: #21252b !important;
          color: #5c6370 !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          text-align: right !important;
          user-select: none !important;
          border-right: 1px solid #444 !important;
          min-width: 40px !important;
          flex-shrink: 0 !important;
          white-space: pre !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
        }

        /* Code content container - pre element in code blocks with line numbers */
        .code-block:not([data-code]) > div > pre {
          flex: 1 !important;
          margin: 0 !important;
          padding: 8px !important;
          background: #282c34 !important;
          color: #abb2bf !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        /* Code blocks without line numbers (data-code attribute) */
        .code-block[data-code] {
          background: #282c34 !important;
          border-radius: 8px !important;
          margin: 8px 0 !important;
        }

        /* Code blocks with data-code that have line numbers (nested div structure) */
        /* Handle both with and without span wrapper */
        .code-block[data-code] > div,
        .code-block[data-code] > span > div {
          display: flex !important;
          background: #282c34 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          margin: 0 !important;
        }

        /* Line numbers container in code blocks with data-code */
        .code-block[data-code] > div > div:first-child,
        .code-block[data-code] > span > div > div:first-child {
          display: block !important;
          padding: 8px 8px 8px 12px !important;
          background: #21252b !important;
          color: #5c6370 !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          text-align: right !important;
          user-select: none !important;
          border-right: 1px solid #444 !important;
          min-width: 40px !important;
          flex-shrink: 0 !important;
          white-space: pre !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
        }

        /* Code content wrapper in code blocks with data-code (new line-by-line structure) */
        .code-block[data-code] > div > div:nth-child(2) {
          flex: 1 !important;
          margin: 0 !important;
          padding: 8px 0 !important;
          background: #282c34 !important;
          overflow-x: auto !important;
        }

        /* Individual line containers within code content wrapper */
        .code-block[data-code] > div > div:nth-child(2) > div[data-line-index] {
          min-height: 19.5px !important;
          line-height: 1.5 !important;
          padding: 0 8px !important;
          color: #abb2bf !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        /* Ensure hljs spans within line containers inherit styles */
        .code-block[data-code] > div > div:nth-child(2) > div[data-line-index] .hljs {
          background: transparent !important;
          color: inherit !important;
        }

        /* Line number divs within line numbers container */
        .code-block[data-code] > div > div[style*='background: #21252b'] > div {
          display: block !important;
          height: 19.5px !important;
          line-height: 1.5 !important;
        }

        /* Code content in code blocks with data-code that have line numbers (old pre structure - for backward compatibility) */
        .code-block[data-code] > div > pre,
        .code-block[data-code] > span > div > pre {
          flex: 1 !important;
          margin: 0 !important;
          padding: 8px !important;
          background: #282c34 !important;
          color: #abb2bf !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        /* Code blocks without line numbers - direct pre child */
        .code-block[data-code] > pre {
          margin: 0 !important;
          padding: 8px !important;
          background: #282c34 !important;
          color: #abb2bf !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        .code-block .hljs {
          background: transparent !important;
          color: inherit !important;
        }
      `}</style>

      {currentImage && (
        <ImagePreviewModal
          src={currentImage.src}
          alt={currentImage.alt}
          isOpen={isOpen}
          onClose={closePreview}
          showDownload={true}
        />
      )}
    </>
  )
}
